import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DeviceConfig from "./DeviceConfiguration/DeviceConfig";
import { Grid, Stack, Typography, ToggleButton, ToggleButtonGroup, Modal, Box, Button, Stepper, Step, 
  StepButton, TextField, MenuItem, FormControl, InputLabel, Select, Dialog, DialogTitle, DialogContent, 
  DialogContentText, DialogActions, useTheme, Menu, FormControlLabel,
  Checkbox} from "@mui/material";
import { NumericFormat } from "react-number-format";
import PropTypes from "prop-types";
import { projectManageAxios as axios } from "../../../utils/AxiosUtility";
import InputAdornment from "@mui/material/InputAdornment";
import AdditionalContractInfo from "./DeviceConfiguration/AdditionalContractInfo";

const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      decimalScale={2}
      fixedDecimalScale
      thousandSeparator
      valueIsNumericString
    />
  );
});

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const steps = ["Customer Info", "Additional Contract Info", "Review Device Configurations"];

const ContractReceived = ({ isContractReceived, Contract, siteData }) => {
  const [contract, setContract] = useState(Contract || false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [installationPrice, setInstallationPrice] = useState(0);
  const [rmrValue, setRmrValue] = useState(0);
  const [centralStation, setCentralStation] = useState(siteData?.centralStation || "Not Selected");
  const [accountManager, setAccountManager] = useState(siteData?.csAccountManager || "Not Selected");
  const [selection, setSelection] = useState("no");

  const [companyName, setCmName] = useState("");
  const [companyAddress1, setCmAddress1] = useState("");
  const [companyAddress2, setCmAddress2] = useState("");
  const [companyCity, setCmCity] = useState("");
  const [companyState, setCmState] = useState("");
  const [companyZip, setCmZip] = useState("");
  const [companyCountry, setCmCountry] = useState("United States");

  const [siteName, setSiName] = useState("");
  const [siteAddress1, setSiAddress1] = useState("");
  const [siteAddress2, setSiAddress2] = useState("");
  const [siteCity, setSiCity] = useState("");
  const [siteState, setSiState] = useState("");
  const [siteZip, setSiZip] = useState("");
  const [siteCountry, setSiCountry] = useState("United States");

  const [international, setInternational] = useState(false);

  const [billingName, setBillingName] = useState("");
  const [billingAddress1, setBillingAddress1] = useState("");
  const [billingAddress2, setBillingAddress2] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingZip, setBillingZip] = useState("");
  const [billingCountry, setBillingCountry] = useState("United States");

  const [existingCustomer, setExistingCustomer] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [cmId, setCmId] = useState("");
  const [existingSite, setExistingSite] = useState(false);
  const [selectedSite, setSelectedSite] = useState("");
  const [csId, setCsId] = useState("");
  const [masterCustomerOptions, setMasterCustomerOptions] = useState([{ id: 0, cmName: "No Options Available" },]);
  const [customerSiteOptions, setCustomerSiteOptions] = useState([{ id: 0, name: "No Options Available" },]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [cafs, setCafs] = useState(false);
  const [suppressionRmr, setSuppressionRmr] = useState(false);
  const [earlyDetectionRmr, setEarlyDetectionRmr] = useState(false);
  const [category, setCategory] = useState("Waste - Landfill");
  const [salesPerson, setSalesPerson] = useState("");
  const [terms, setTerms] = useState("Due on Receipt");

  const [msaDate, setMsaDate] = useState(null);
  const [sowDate, setSowDate] = useState(null);
  const [autoRenewalPeriod, setAutoRenewal] = useState(0);
  const [initialTerms, setInitialTerms] = useState("1 Year");
  const [cancellationNotice, setCancellationNotice] = useState(0);
  const [requiredInspections, setRequiredInspections] = useState('No Requirement');
  const [liabilityLimitAmount, setLiabilityLimitAmount] = useState(0);
  const [companyLegalName, setCompanyLegalName] = useState('');
  const [stateOfOrganization, setStateOfOrganization] = useState('');
  const [legalEntityType, setLegalEntityType] = useState('');
  const [assignmentProvision, setAssignmentProvision] = useState('Freely Assignable');
  const [installationCode, setInstallationCode] = useState('None');

  const outlineColor = theme.palette.mode === "dark" ? "white" : "red";

  useEffect(() => {
    fetchMasterCustomerData();
    fetchPreLeadData();
  }, []);

  const fetchPreLeadData = async () => {
    try {
      const response = await axios.get(`/api/PreSales/pslead/${searchParams.get("id")}`);
      setCmName(response.data.companyName || "");
      setCmAddress1(response.data.companyAddress1 || "");
      setCmAddress2(response.data.companyAddress2 || "");
      setCmCity(response.data.companyCity || "");
      setCmState(response.data.companyState || "");
      setCmZip(response.data.companyZip || "");
      setCmCountry(response.data.companyCountry || "");
      setSiName(response.data.siteName || "");
      setSiAddress1(response.data.siteAddress1 || "");
      setSiAddress2(response.data.siteAddress2 || "");
      setSiCity(response.data.siteCity || "");
      setSiState(response.data.siteState || "");
      setSiZip(response.data.siteZip || "");
      setSiCountry(response.data.siteCountry || "");
      setBillingName(response.data.companyName || "");
      setBillingAddress1(response.data.companyAddress1 || "");
      setBillingAddress2(response.data.companyAddress2 || "");
      setBillingCity(response.data.companyCity || "");
      setBillingState(response.data.companyState || "");
      setBillingZip(response.data.companyZip || "");
      setBillingCountry(response.data.companyCountry || "");
      setRmrValue(response.data.rmrValue || 0);
      setInstallationPrice(response.data.installationPrice || 0);
      setSalesPerson(response.data.salesPerson || 0);
    } catch (error) {
      console.error("Error fetching master customer data:", error);
    }
  };

  const fetchMasterCustomerData = async () => {
    try {
      const response = await axios.get("/api/PreSales/masterCustomers");
      setMasterCustomerOptions(response.data || []);
    } catch (error) {
      console.error("Error fetching master customer data:", error);
      setMasterCustomerOptions([]);
    }
  };

  const fetchCustomerSiteData = async (cmId) => {
    try {
      const response = await axios.get(
        `/api/PreSales/sitesByCMId/${cmId}`
      );
      setCustomerSiteOptions(response.data || []);
    } catch (error) {
      console.error("Error fetching customer site data:", error);
      setCustomerSiteOptions([]);
    }
  };

  const handleMasterCustomerSelect = (event) => {
    const selectedCustomer = event.target.value;
    setSelectedCustomer(selectedCustomer);
    setCmId(selectedCustomer.cmId);
  
    setCmName(selectedCustomer.cmName || "");
    setCmAddress1(selectedCustomer.cmAddress1 || "");
    setCmAddress2(selectedCustomer.cmAddress2 || "");
    setCmCity(selectedCustomer.cmCity || "");
    setCmState(selectedCustomer.cmState || "");
    setCmZip(selectedCustomer.cmZip || "");
    setCmCountry(selectedCustomer.cmCountry || "United States");
  
    fetchCustomerSiteData(selectedCustomer.cmId);
  };
  
  const handleCustomerSiteSelect = (event) => {
    const selectedSite = event.target.value;
    setSelectedSite(selectedSite);
    setCsId(selectedSite.csId);
  
    setSiName(selectedSite.csName || "");
    setSiAddress1(selectedSite.csAddress1 || "");
    setSiAddress2(selectedSite.csAddress2 || "");
    setSiCity(selectedSite.csCity || "");
    setSiState(selectedSite.csState || "");
    setSiZip(selectedSite.csZip || "");
    setSiCountry(selectedSite.csCountry || "United States");
  };

  const handleContract = (event, newValue) => {
    if (!contract && newValue === true) {
      setOpen(true);
    }
    setContract(newValue);
  };

  const handleSelection = (event, newSelection) => {
    if (newSelection !== null) {
      setSelection(newSelection);
    }
  };

  const handleCentralStationSelect = (event) => {
    setCentralStation(event.target.value);
  };

  const handleAccountManagerSelect = (event) => {
    setAccountManager(event.target.value);
  };

  const handleCategorySelect = (event) => {
    setCategory(event.target.value);
  };

  const handleSalesRepSelect = (event) => {
    setSalesPerson(event.target.value);
  };

  const handleTermSelect = (event) => {
    setTerms(event.target.value);
  };

  const countryOptions = [
    {
      id: 0,
      name: "United States",
    },
    {
      id: 1,
      name: "United Kingdom",
    },
    {
      id: 2,
      name: "France",
    },
    {
      id: 3,
      name: "Australia",
    },
    {
      id: 4,
      name: "Canada",
    },
  ];

  const centralStationOptions = [
    {
      id: 0,
      name: "Not Selected",
    },
    {
      id: 1,
      name: "COPS",
    },
    {
      id: 2,
      name: "Acadian",
    },
  ];

  const accountManagerOptions = [
    {
      id: 0,
      name: "Not Selected",
    },
    {
      id: 1,
      name: "Sasha Hartje",
    },
    {
      id: 2,
      name: "Joe Chudy",
    },
    {
      id: 3,
      name: "Joey Hannah",
    },
  ];

  const categoryOptions = [
    {
      id: 0,
      name: "Agriculture",
    },
    {
      id: 1,
      name: "Aviation",
    },
    {
      id: 2,
      name: "Distribution/ASRS",
    },
    {
      id: 3,
      name: "Manufacturing",
    },
    {
      id: 4,
      name: "Metal Recycling - Scrap",
    },
    {
      id: 5,
      name: "Paper/Pulp/Lumber",
    },
    {
      id: 6,
      name: "Refining",
    },
    {
      id: 7,
      name: "Transportation - Fleet",
    },
    {
      id: 8,
      name: "Waste - Electronic",
    },
    {
      id: 9,
      name: "Waste - Energy",
    },
    {
      id: 10,
      name: "Waste - Hazardous",
    },
    {
      id: 11,
      name: "Waste - Landfill",
    },
    {
      id: 12,
      name: "Waste - Mixed or Other",
    },
    {
      id: 13,
      name: "Waste - MRF",
    },
    {
      id: 14,
      name: "Waste - Transfer",
    },
  ];

  const salesRepOptions = [
    {
      id: 0,
      name: "Lindsey Scharg",
    },
    {
      id: 1,
      name: "Ryan Fogelman",
    },
    {
      id: 2,
      name: "Sid Soeun",
    },
    {
      id: 3,
      name: "House",
    },
  ];

  const termsOptions = [
    {
      id: 0,
      name: ".5% Net 10",
    },
    {
      id: 1,
      name: "1% Net 10",
    },
    {
      id: 2,
      name: "2% Net 10",
    },
    {
      id: 3,
      name: "Due on Receipt",
    },
    {
      id: 4,
      name: "Net 15",
    },
    {
      id: 5,
      name: "Net 30",
    },
    {
      id: 6,
      name: "Net 45",
    },
    {
      id: 7,
      name: "Net 60",
    },
    {
      id: 8,
      name: "Net 75",
    },
    {
      id: 9,
      name: "Prepay",
    },
  ];

  const handleModalClose = () => {
    if (open && !loading) {
      setContract(false);
    }
    setOpen(false);
  };

  const handleStep = (step) => () => setActiveStep(step);
  const handleNext = () => setActiveStep((prevStep) => prevStep + 1);
  const handleBack = () => setActiveStep((prevStep) => prevStep - 1);

  const handleCheckboxChange = (option) => {
    if (option === "CAFS") {
      setCafs((prev) => !prev);
    } else if (option === "Suppression RMR") {
      setSuppressionRmr((prev) => !prev);
    } else if (option === "Early Detection RMR") {
      setEarlyDetectionRmr((prev) => !prev);
    } else if (option === "International") {
      setInternational((prev) => !prev);
    }
  };

  const handleConfirm = () => {
    setConfirmDialogOpen(true);
  };

  const handleSave = () => {
    setLoading(true);
    try {
      isContractReceived({
        contractReceived: true,
        installationPrice,
        rmrValue,
        centralStation,
        accountManager,
        companyName,
        companyAddress1,
        companyAddress2,
        companyCity,
        companyState,
        companyZip,
        companyCountry,
        siteName,
        siteAddress1,
        siteAddress2,
        siteCity,
        siteState,
        siteZip,
        siteCountry,
        billingName,
        billingAddress1,
        billingAddress2,
        billingCity,
        billingState,
        billingZip,
        billingCountry,
        existingCustomer,
        cmId,
        csId,
        selectedCustomer,
        existingSite,
        selectedSite,
        category,
        cafs,
        suppressionRmr,
        earlyDetectionRmr,
        salesPerson,
        terms,
        msaDate,
        sowDate,
        autoRenewalPeriod,
        initialTerms,
        cancellationNotice,
        requiredInspections,
        liabilityLimitAmount,
        companyLegalName,
        stateOfOrganization,
        legalEntityType,
        assignmentProvision,
        installationCode,
        international,
      });
      setOpen(false);
    } catch (error) {
      alert("Error during contract received confirmation. Please try again.");
    }
    setLoading(false);
  };

  const handleConfirmClose = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <Grid item xs={12} style={{ paddingBottom: "5%" }}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h6" style={{ minWidth: "35%" }}>
            Contract Received
          </Typography>

          <ToggleButtonGroup
            color="primary"
            value={contract}
            disabled={contract}
            exclusive
            onChange={handleContract}
            aria-label="Contract Received"
            style={{ flexGrow: "1", minWidth: "60%" }}
          >
            <ToggleButton color="success" value={true}>
              Yes
            </ToggleButton>
            <ToggleButton value={false}>No</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Grid>

      <Modal open={open} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "75%",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            paddingLeft: 3,
            paddingRight: 3,
            overflowY: "auto",
            maxHeight: "90vh",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.paper",
              zIndex: 2,
              height: "100px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "10px",
            }}
          >
            <Stepper nonLinear activeStep={activeStep} sx={{ mb: 2 }}>
              {steps.map((label, index) => (
                <Step key={label} completed={completed[index]}>
                  <StepButton color="inherit" onClick={handleStep(index)}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box sx={{ p: 2 }}>
            {activeStep === 0 ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Stack direction="column" spacing={2} sx={{ paddingRight: "10%" }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography>Existing Master Customer</Typography>
                      <ToggleButtonGroup
                        color="primary"
                        value={existingCustomer}
                        exclusive
                        onChange={(event, newValue) => {
                          setExistingCustomer(newValue);
                        }}
                        aria-label="Master Customer"
                      >
                        <ToggleButton color="success" value={true}>
                          Yes
                        </ToggleButton>
                        <ToggleButton value={false}>No</ToggleButton>
                      </ToggleButtonGroup>

                      {existingCustomer && (
                        <FormControl sx={{ width: "50%" }}>
                          <InputLabel id="master-customer-label">
                            Existing Master Customer
                          </InputLabel>
                          <Select
                            labelId="master-customer-label"
                            id="master-customer-select"
                            value={selectedCustomer}
                            label="Existing Master Customer"
                            onChange={handleMasterCustomerSelect}
                          >
                            {(masterCustomerOptions || []).map((cm) => (
                              <MenuItem key={cm.id} value={cm}>
                                {cm.cmName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>

                    {selectedCustomer && existingCustomer && masterCustomerOptions.length > 0 && (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography>Existing Customer Site</Typography>
                        <ToggleButtonGroup
                          color="primary"
                          value={existingSite}
                          exclusive
                          onChange={(event, newValue) => {
                            setExistingSite(newValue);
                          }}
                          aria-label="Site Customer"
                        >
                          <ToggleButton color="success" value={true}>
                            Yes
                          </ToggleButton>
                          <ToggleButton value={false}>No</ToggleButton>
                        </ToggleButtonGroup>

                        {existingSite && (
                          <FormControl sx={{ width: "50%" }}>
                            <InputLabel id="site-customer-label">
                              Existing Customer Site
                            </InputLabel>
                            <Select
                              labelId="site-customer-label"
                              id="site-customer-select"
                              value={selectedSite}
                              label="Existing Customer Site"
                              onChange={handleCustomerSiteSelect}
                            >
                              {(customerSiteOptions || []).map((cs) => (
                                <MenuItem key={cs.id} value={cs}>
                                  {cs.csName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Grid>

                <Grid container spacing={2} sx={{paddingTop: 4}}>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          border: 1,
                          borderColor: "primary",
                          borderRadius: 1,
                          padding: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, textAlign: "center" }}
                        >
                          Master Customer
                        </Typography>
                        <TextField
                          label="Name"
                          variant="filled"
                          value={selectedCustomer.cmName || companyName}
                          onChange={(e) => setCmName(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="Address"
                          variant="filled"
                          value={selectedCustomer.cmAddress1 || companyAddress1}
                          onChange={(e) => setCmAddress1(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="Address 2"
                          variant="filled"
                          value={selectedCustomer.cmAddress2 || companyAddress2}
                          onChange={(e) => setCmAddress2(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="City"
                          variant="filled"
                          value={selectedCustomer.cmCity || companyCity}
                          onChange={(e) => setCmCity(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="State"
                          variant="filled"
                          value={selectedCustomer.cmState || companyState}
                          onChange={(e) => setCmState(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="Zip Code"
                          variant="filled"
                          value={selectedCustomer.cmZip || companyZip}
                          onChange={(e) => setCmZip(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="cs-label">Country</InputLabel>
                            <Select
                              variant="filled"
                              labelId="cs-label"
                              id="cs-select"
                              value={selectedCustomer.cmCountry || companyCountry}
                              label="Central Station"
                              onChange={(e) => setCmCountry(e.target.value)}
                            >
                              {countryOptions.map((cs) => (
                                <MenuItem key={cs.id} value={cs.name}>
                                  {cs.name}
                                </MenuItem>
                              ))}
                            </Select>
                        </FormControl>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          border: 1,
                          borderColor: "primary",
                          borderRadius: 1,
                          padding: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, textAlign: "center" }}
                        >
                          Site Information
                        </Typography>
                        <TextField
                          label="Name"
                          variant="filled"
                          value={selectedSite.csName || siteName}
                          onChange={(e) => setSiName(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="Address"
                          variant="filled"
                          value={selectedSite.csAddress1 || siteAddress1}
                          onChange={(e) => setSiAddress1(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="Address 2"
                          variant="filled"
                          value={selectedSite.csAddress2 || siteAddress2}
                          onChange={(e) => setSiAddress2(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="City"
                          variant="filled"
                          value={selectedSite.csCity || siteCity}
                          onChange={(e) => setSiCity(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="State"
                          variant="filled"
                          value={selectedSite.csState || siteState}
                          onChange={(e) => setSiState(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          label="Zip Code"
                          variant="filled"
                          value={selectedSite.csZip || siteZip}
                          onChange={(e) => setSiZip(e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="cs-label">Country</InputLabel>
                            <Select
                              variant="filled"
                              labelId="cs-label"
                              id="cs-select"
                              value={selectedSite.csCountry || siteCountry}
                              label="Central Station"
                              onChange={(e) => setSiCountry(e.target.value)}
                            >
                              {countryOptions.map((cs) => (
                                <MenuItem key={cs.id} value={cs.name}>
                                  {cs.name}
                                </MenuItem>
                              ))}
                            </Select>
                        </FormControl>
                      </Box>
                    </Grid>

                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        border: 1,
                        borderColor: "primary",
                        borderRadius: 1,
                        padding: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, textAlign: "center" }}
                      >
                        Billing Information
                      </Typography>
                      <TextField
                        label="Name"
                        variant="filled"
                        value={selectedCustomer.cmName || billingName}
                        onChange={(e) => setBillingName(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label="Address"
                        variant="filled"
                        value={selectedCustomer.cmAddress1 || billingAddress1}
                        onChange={(e) => setBillingAddress1(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label="Address 2"
                        variant="filled"
                        value={selectedCustomer.cmAddress2 || billingAddress2}
                        onChange={(e) => setBillingAddress2(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label="City"
                        variant="filled"
                        value={selectedCustomer.cmCity || billingCity}
                        onChange={(e) => setBillingCity(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label="State"
                        variant="filled"
                        value={selectedCustomer.cmState || billingState}
                        onChange={(e) => setBillingState(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label="Zip Code"
                        variant="filled"
                        value={selectedCustomer.cmZip || billingZip}
                        onChange={(e) => setBillingZip(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <FormControl fullWidth>
                            <InputLabel id="cs-label">Country</InputLabel>
                            <Select
                              variant="filled"
                              labelId="cs-label"
                              id="cs-select"
                              value={selectedCustomer.cmCountry || billingCountry}
                              label="Central Station"
                              onChange={(e) => setBillingCountry(e.target.value)}
                            >
                              {countryOptions.map((cs) => (
                                <MenuItem key={cs.id} value={cs.name}>
                                  {cs.name}
                                </MenuItem>
                              ))}
                            </Select>
                        </FormControl>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        border: 1,
                        borderColor: "primary",
                        borderRadius: 1,
                        padding: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, textAlign: "center" }}
                      >
                        Additional Information
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="RMR Value"
                            required
                            value={rmrValue}
                            onChange={(e) => setRmrValue(e.target.value)}
                            name="rmrValue"
                            id="rmr-value-input"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                              ),
                              inputComponent: NumericFormatCustom,
                            }}
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Install Value"
                            required
                            value={installationPrice}
                            onChange={(e) => setInstallationPrice(e.target.value)}
                            name="installValue"
                            id="install-price-input"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                              ),
                              inputComponent: NumericFormatCustom,
                            }}
                            variant="outlined"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="cs-label">Central Station</InputLabel>
                            <Select
                              labelId="cs-label"
                              id="cs-select"
                              value={centralStation}
                              label="Central Station"
                              onChange={handleCentralStationSelect}
                            >
                              {centralStationOptions.map((cs) => (
                                <MenuItem key={cs.id} value={cs.name}>
                                  {cs.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="am-label">Account Manager</InputLabel>
                            <Select
                              labelId="am-label"
                              id="am-select"
                              value={accountManager}
                              label="Account Manager"
                              onChange={handleAccountManagerSelect}
                            >
                              {accountManagerOptions.map((am) => (
                                <MenuItem key={am.id} value={am.name}>
                                  {am.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="am-label">Category</InputLabel>
                            <Select
                              labelId="am-label"
                              id="am-select"
                              value={category}
                              label="Category"
                              onChange={handleCategorySelect}
                            >
                              {categoryOptions.map((am) => (
                                <MenuItem key={am.id} value={am.name}>
                                  {am.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="am-label">Sales Rep</InputLabel>
                            <Select
                              labelId="am-label"
                              id="am-select"
                              value={salesPerson}
                              label="Status"
                              onChange={handleSalesRepSelect}
                            >
                              {salesRepOptions.map((am) => (
                                <MenuItem key={am.id} value={am.name}>
                                  {am.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="am-label">Terms</InputLabel>
                            <Select
                              labelId="term-label"
                              id="term-select"
                              value={terms}
                              label="Terms"
                              onChange={handleTermSelect}
                            >
                              {termsOptions.map((am) => (
                                <MenuItem key={am.id} value={am.name}>
                                  {am.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "flex-start",
                              padding: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ mb: 2, textAlign: "center" }}
                            >
                              Customer Type
                            </Typography>
                            {["CAFS", "Suppression RMR", "Early Detection RMR", "International"].map((option) => (
                              <FormControlLabel
                                key={option}
                                control={
                                  <Checkbox
                                    onChange={() => handleCheckboxChange(option)}
                                    checked={
                                      option === "CAFS"
                                        ? cafs
                                        : option === "Suppression RMR"
                                        ? suppressionRmr
                                        : option === "Early Detection RMR"
                                        ? earlyDetectionRmr
                                        : international
                                    }
                                    sx={{
                                      color: outlineColor,
                                      "&.Mui-checked": {
                                        color: outlineColor,
                                      },
                                    }}
                                  />
                                }
                                label={option}
                                sx={{ color: outlineColor }}
                              />
                            ))}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            ) : activeStep === 1 ? (
              <Box>
                <AdditionalContractInfo
                  msaDate={msaDate}
                  setMsaDate={setMsaDate}
                  sowDate={sowDate}
                  setSowDate={setSowDate}
                  autoRenewalPeriod={autoRenewalPeriod}
                  setAutoRenewal={setAutoRenewal}
                  initialTerms={initialTerms}
                  setInitialTerms={setInitialTerms}
                  cancellationNotice={cancellationNotice}
                  setCancellationNotice={setCancellationNotice}
                  requiredInspections={requiredInspections}
                  setRequiredInspections={setRequiredInspections}
                  liabilityLimitAmount={liabilityLimitAmount}
                  setLiabilityLimitAmount={setLiabilityLimitAmount}
                  companyLegalName={companyLegalName}
                  setCompanyLegalName={setCompanyLegalName}
                  stateOfOrganization={stateOfOrganization}
                  setStateOfOrganization={setStateOfOrganization}
                  legalEntityType={legalEntityType}
                  setLegalEntityType={setLegalEntityType}
                  assignmentProvision={assignmentProvision}
                  setAssignmentProvision={setAssignmentProvision}
                  installationCode={installationCode}
                  setInstallationCode={setInstallationCode}
                />
              </Box>
            ) : (
              <Box>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Typography variant="h6">Review Device Configurations</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={selection}
                    exclusive
                    onChange={handleSelection}
                    aria-label="device configuration review"
                  >
                    <ToggleButton color="success" value="yes">
                      Yes
                    </ToggleButton>
                    <ToggleButton value="no">No</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                <Box sx={{ mt: 2 }}>
                  <DeviceConfig />
                </Box>
              </Box>
            )}

            <Dialog
              open={confirmDialogOpen}
              keepMounted
              onClose={handleConfirmClose}
              aria-describedby="alert-dialog-slide-description"
            >
              <DialogTitle>{"Confirm Contract Received?"}</DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-slide-description">
                  Setting this to complete will set the status of this task
                  to complete and open the Site Prep and Site Coordination tasks.
                  Please confirm to proceed.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleSave} variant="outlined" style={{ color: "green" }}>
                  Confirm
                </Button>
                <Button onClick={handleConfirmClose} variant="outlined" color="primary">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            <Box sx={{ display: "flex", flexDirection: "row, pt: 2", paddingTop: 2}}>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              {activeStep < steps.length - 1 ? (
                <Button variant="outlined" onClick={handleNext} sx={{ mr: 1 }}>
                  Next
                </Button>
              ) : (
                <Button
                  style={{ color: selection === "yes" ? "green" : "grey" }}
                  variant="outlined"
                  onClick={handleConfirm}
                  disabled={selection !== "yes"}
                >
                  Save
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ContractReceived;
